import { Outlet, useNavigate } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { AvailableModules, Features } from 'generated/featureDefinitions'
import { TimeoffRootPageDocument } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import ErrorBoundary from 'modules/core/components/ErrorBoundary'
import Loading from 'modules/core/components/Loading'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import type { ResponsivePagePrimaryAction } from 'modules/layout/components/ResponsivePage/_PageHeader/types'
import TimeoffCard from 'modules/timeoff/components/Card'
import CollectionsBootstrap from 'modules/timeoff/lib/CollectionsBootstrap'
import { LeavesContextProvider } from 'modules/timeoff/lib/LeavesContextProvider'
import { ContractsVersionProvider } from 'modules/timeoff/lib/contractsVersionContext'
import policyTimelines from 'modules/timeoff/store/collections/timeoffPolicies/policyTimelines'

const Container = () => {
  const { company } = useCurrentSession()
  const navigate = useNavigate()
  const { navigation } = useNavigation()

  const { data, loading, error } = useQuery(TimeoffRootPageDocument, {
    variables: {
      companyId: company.id,
      moduleId: AvailableModules.timeoff,
      featureId: Features.timeoff_manual_adjustment_options,
    },
  })

  const timeoffEnabled = data?.companies.moduleAvailabilities[0]?.available ?? false
  const canManageTimeoff = data?.timeoff?._policies.companyManageTimeoff.can ?? false
  const hasManualAdjustmentOptions =
    data?.companies.featureAvailabilitiesConnection.nodes[0]?.available ?? false

  const actions = () => {
    const availableActions: ResponsivePagePrimaryAction[] = []

    if (canManageTimeoff) {
      availableActions.push({
        label: '',
        icon: 'settings',
        onClick: settingsOnClick,
      })
    }

    return {
      primary: availableActions,
    }
  }

  const settingsOnClick = () => {
    const timeOffPolicyId = policyTimelines.timeline?.current?.timeoff_policy_id
    if (!timeOffPolicyId) return
    navigate(navigation.settings.timeoffPolicies.id.details.fullPath({ id: timeOffPolicyId }))
  }

  if (loading) {
    return <Loading />
  }

  const employeeData = data?.apiCore.currentsConnection.edges[0]?.node.employee
  const employeeCanReadLeaves = employeeData?._policies?.readLeaves.can ?? false

  if (error || !employeeData || !timeoffEnabled || !employeeCanReadLeaves) {
    return (
      <ResponsivePage.Body variant="full" showHeader={false}>
        <Box padding="s0">
          <ErrorBoundary />
        </Box>
      </ResponsivePage.Body>
    )
  }

  return (
    <ContractsVersionProvider employeeId={employeeData.id}>
      <LeavesContextProvider employeeId={employeeData.id}>
        <ResponsivePage.Body variant="full" showHeader={false} actions={actions()}>
          <TimeoffCard
            employeeData={employeeData}
            hasManualAdjustmentOptions={hasManualAdjustmentOptions}
          >
            <Outlet />
          </TimeoffCard>
        </ResponsivePage.Body>
      </LeavesContextProvider>
    </ContractsVersionProvider>
  )
}

const RootPage = () => {
  return (
    <CollectionsBootstrap>
      <Container />
    </CollectionsBootstrap>
  )
}

export default observer(RootPage)
